import {useEffect, useRef, useState} from 'react';
import {runAutomationAgent} from './agentService';
import {applyAgentOperations} from './draftOperations';
import {MAX_AGENT_IMAGES, readAgentImage} from './images';
import {AgentApplyResult, AgentDraft, AgentImage, AgentMessage, AgentMode, AgentOperation, AgentProposal, AgentReply, FLOW_CHANGED_MESSAGE} from './types';

export interface AgentEditorAccess {
    getDraft: () => AgentDraft;
    applyChanges: (draft: AgentDraft, operations: AgentOperation[]) => AgentApplyResult;
}

/** The loop's own budget is 240 s; the browser gives up a minute later. */
export const AGENT_TIMEOUT_MS = 300_000;
/** The API refuses a request with more messages (`AutomationAgentService.MAX_MESSAGES`), hidden ones included. */
const MAX_AGENT_MESSAGES = 30;
const REPAIR_MESSAGES = 2;
const TIMEOUT_MESSAGE = 'The agent took longer than five minutes to respond. Try a smaller request.';
const PROPOSAL_NOTICE = 'The changes are waiting for Apply.';
const PROPOSAL_DISCARDED_NOTICE = 'Discarded. Your flow has not changed.';
const PROPOSAL_STALE_NOTICE = 'The flow changed after I proposed these changes, so I discarded them. Your flow has not changed — ask again and I will work from your latest draft.';

const assistantMessage = (reply: AgentReply, extra: Partial<AgentMessage> = {}): AgentMessage => ({
    id: crypto.randomUUID(), role: 'assistant', text: reply.message, questions: reply.questions, notes: reply.notes, ...extra,
});

const repairMessage = (reason: string): string =>
    `The editor rejected the changes: ${reason.replace(/\.\s*$/, '')}. Correct the batch against the current draft.`;

/** State lives only in this mounted editor; every request carries the whole conversation. */
export const useAgentConversation = (access: AgentEditorAccess) => {
    const [messages, setMessages] = useState<AgentMessage[]>([]);
    const [text, setText] = useState('');
    const [images, setImages] = useState<AgentImage[]>([]);
    const [busy, setBusy] = useState(false);
    const [readingImages, setReadingImages] = useState(false);
    const [error, setError] = useState('');
    const [retryMessages, setRetryMessages] = useState<AgentMessage[] | null>(null);
    const [activity, setActivity] = useState<string[]>([]);
    const [mode, setMode] = useState<AgentMode>('apply');
    const [proposal, setProposal] = useState<AgentProposal | null>(null);
    const request = useRef<AbortController | null>(null);
    const reading = useRef(false);
    const session = useRef(0);
    const accessRef = useRef(access);
    accessRef.current = access;
    const modeRef = useRef(mode);
    modeRef.current = mode;
    /** The assistant message a pending proposal belongs to; it receives the `applied` summary. */
    const proposalMessageId = useRef('');

    useEffect(() => () => {
        request.current?.abort();
        request.current = null;
        session.current += 1;
    }, []);

    const attach = async (files: File[]): Promise<string | undefined> => {
        if (reading.current || request.current || !files.length) return;
        reading.current = true;
        setReadingImages(true);
        const currentSession = session.current;
        try {
            const previous = messages.flatMap(message => message.images || []).concat(images);
            const remaining = Math.max(0, MAX_AGENT_IMAGES - previous.length);
            const limitMessage = files.length > remaining ? 'You can attach up to 3 images per conversation.' : undefined;
            if (!remaining) return limitMessage;
            const additions = await Promise.all(files.slice(0, remaining).map(readAgentImage));
            // `data` is base64: 16 MiB of it is about 12 MB of images.
            if (previous.concat(additions).reduce((total, image) => total + image.data.length, 0) > 16 * 1024 * 1024) {
                throw new Error('Keep the combined image size below 12 MB.');
            }
            if (session.current === currentSession) setImages(current => [...current, ...additions]);
            return limitMessage;
        } catch (failure) {
            if (session.current === currentSession) return (failure as Error).message;
        } finally {
            reading.current = false;
            if (session.current === currentSession) setReadingImages(false);
        }
    };

    /**
     * A batch the editor rejects gets one hidden repair turn — never for a stale draft, never past the
     * message cap. In propose mode a valid batch becomes the pending proposal instead.
     */
    const respond = async (history: AgentMessage[]) => {
        if (request.current) return;
        const controller = new AbortController();
        request.current = controller;
        let timedOut = false;
        const timer = setTimeout(() => {timedOut = true; controller.abort();}, AGENT_TIMEOUT_MS);
        setBusy(true);
        setError('');
        setRetryMessages(null);
        setActivity([]);
        try {
            let turn = history;
            let repaired = false;
            for (;;) {
                setActivity([]);
                const draft = accessRef.current.getDraft();
                const reply = await runAutomationAgent(turn, draft, {mode: modeRef.current, signal: controller.signal,
                    onProgress: progress => {if (!controller.signal.aborted) setActivity(current => [...current, progress.label]);}});
                if (controller.signal.aborted) return;
                if (!reply.operations.length) {
                    setMessages([...turn, assistantMessage(reply)]);
                    return;
                }
                let rejection: string;
                try {
                    if (modeRef.current === 'propose') {
                        // A dry run of the same transaction the editor will run on Apply — React Flow state is untouched.
                        const {summary} = applyAgentOperations(draft, reply.operations);
                        const message = assistantMessage(reply, {text: [reply.message, PROPOSAL_NOTICE].filter(Boolean).join('\n\n')});
                        proposalMessageId.current = message.id;
                        setProposal({draft, operations: reply.operations, summary});
                        setMessages([...turn, message]);
                    } else {
                        const applied = accessRef.current.applyChanges(draft, reply.operations).summary;
                        setMessages([...turn, assistantMessage(reply, {applied})]);
                    }
                    return;
                } catch (failure) {
                    rejection = (failure as Error).message;
                }
                if (rejection !== FLOW_CHANGED_MESSAGE && !repaired && turn.length + REPAIR_MESSAGES <= MAX_AGENT_MESSAGES) {
                    repaired = true;
                    turn = [...turn,
                        {id: crypto.randomUUID(), role: 'assistant', text: reply.message, notes: reply.notes, hidden: true},
                        {id: crypto.randomUUID(), role: 'user', text: repairMessage(rejection), hidden: true}];
                    setMessages(turn);
                    continue;
                }
                setMessages([...turn, {id: crypto.randomUUID(), role: 'assistant',
                    text: `I couldn’t apply these changes: ${rejection} Your flow has not changed.`, notes: reply.notes}]);
                return;
            }
        } catch (failure) {
            if (!controller.signal.aborted || timedOut) {
                setError(timedOut ? TIMEOUT_MESSAGE : failure instanceof Error ? failure.message : 'The agent could not respond. Try again.');
                setRetryMessages(history);
            }
        } finally {
            clearTimeout(timer);
            if (request.current === controller) {
                request.current = null;
                setBusy(false);
                setActivity([]);
            }
        }
    };

    const send = () => {
        if (request.current || reading.current || (!text.trim() && !images.length)) return;
        if (messages.length >= MAX_AGENT_MESSAGES) {
            setError('Start a new conversation to continue. Your canvas will stay as it is.');
            return;
        }
        const history: AgentMessage[] = [...messages, {id: crypto.randomUUID(), role: 'user',
            text: text.trim() || 'Help me recreate this workflow from the attached image.', images}];
        setMessages(history);
        setText('');
        setImages([]);
        void respond(history);
    };

    const stop = () => {
        if (!request.current) return;
        request.current?.abort();
        request.current = null;
        setBusy(false);
        setActivity([]);
        setMessages(current => [...current, {id: crypto.randomUUID(), role: 'assistant', text: 'Stopped. No changes were applied.'}]);
    };

    const applyProposal = () => {
        const pending = proposal;
        if (!pending || request.current) return;
        try {
            const applied = accessRef.current.applyChanges(pending.draft, pending.operations).summary;
            const messageId = proposalMessageId.current;
            setProposal(null);
            setError('');
            setMessages(current => current.map(message => message.id === messageId ? {...message, applied} : message));
        } catch (failure) {
            const reason = (failure as Error).message;
            if (reason === FLOW_CHANGED_MESSAGE) {
                setProposal(null);
                setMessages(current => [...current, {id: crypto.randomUUID(), role: 'assistant', text: PROPOSAL_STALE_NOTICE}]);
            } else {
                setError(reason);
            }
        }
    };

    const discardProposal = () => {
        if (!proposal) return;
        setProposal(null);
        setMessages(current => [...current, {id: crypto.randomUUID(), role: 'assistant', text: PROPOSAL_DISCARDED_NOTICE}]);
    };

    const reset = () => {
        request.current?.abort();
        request.current = null;
        session.current += 1;
        setBusy(false);
        setReadingImages(false);
        setMessages([]);
        setImages([]);
        setText('');
        setError('');
        setRetryMessages(null);
        setActivity([]);
        setProposal(null);
        proposalMessageId.current = '';
    };

    return {messages, text, setText, images, removeImage: (id: string) => setImages(current => current.filter(image => image.id !== id)),
        busy, readingImages, error, activity, mode, setMode, proposal, applyProposal, discardProposal,
        attach, send, stop, reset, retry: retryMessages ? () => void respond(retryMessages) : undefined};
};
